import React, { useState, useEffect } from 'react';
import ManagementLayout from '../layout/ManagementLayout';
import Breadcrumbs from './Breadcrumbs';
import FolderCard from './FolderCard';
import FileTable from './FileTable';
import CreateFolderModal from './CreateFolderModal';
import UploadModal from './UploadModal';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';
import useAuth from '../../auth/useAuth';
import { fetchMediaLibrary } from '../../api/mediaApi';
import { createFolder } from '../../api/folderApi';

export default function MediaLibrary() {
  const { user } = useAuth();
  const canWrite = ['uploader', 'manager', 'admin'].includes(user?.role);

  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const load = async (path) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchMediaLibrary(path);
      setFolders(res.data.folders);
      setFiles(res.data.files);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(currentPath); }, [currentPath]);

  const navigateTo = (path) => {
    setCurrentPath(path);
    setSearch('');
  };

  const openFolder = (folder) => {
    setCurrentPath(folder.key);
    setSearch('');
  };

  const handleCreateFolder = async (folderName) => {
    await createFolder({ path: currentPath, folderName });
    load(currentPath);
  };

  const breadcrumbParts = currentPath.replace(/\/$/, '').split('/').filter(Boolean);

  const filteredFolders = folders.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredFiles = files.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <ManagementLayout>
      <div className="mol-page">
        <div className="mol-page__header">
          <div>
            <h2 className="mol-page__title">Media Library</h2>
            <p className="mol-page__subtitle">Browse and manage your media files and folders.</p>
          </div>
        </div>

        <div className="mol-toolbar">
          <div className="mol-toolbar__search">
            <span className="mol-toolbar__search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files and folders..."
              className="mol-toolbar__search-input"
            />
          </div>
          {canWrite && (
            <>
              <div className="mol-toolbar__divider" />
              <div className="mol-toolbar__actions">
                <button className="mol-btn mol-btn--secondary mol-btn--sm" onClick={() => setShowCreateFolder(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Create Folder
                </button>
                <button className="mol-btn mol-btn--primary mol-btn--sm" onClick={() => setShowUpload(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Upload Files
                </button>
              </div>
            </>
          )}
        </div>

        <Breadcrumbs parts={breadcrumbParts} onNavigate={navigateTo} />

        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(currentPath)} />
        ) : filteredFolders.length === 0 && filteredFiles.length === 0 ? (
          <EmptyState
            title="This folder is empty"
            message={canWrite ? 'Create a folder or upload files to get started.' : 'No content available yet.'}
          />
        ) : (
          <>
            {filteredFolders.length > 0 && (
              <div className="mol-section">
                <h3 className="mol-section__title">Folders</h3>
                <div className="mol-grid mol-grid--4">
                  {filteredFolders.map((f) => (
                    <FolderCard key={f.key} folder={f} onClick={() => openFolder(f)} />
                  ))}
                </div>
              </div>
            )}
            {filteredFiles.length > 0 && (
              <div className="mol-section">
                <h3 className="mol-section__title">Files</h3>
                <FileTable files={filteredFiles} canDelete={canWrite} onRefresh={() => load(currentPath)} />
              </div>
            )}
          </>
        )}

        <CreateFolderModal
          isOpen={showCreateFolder}
          onClose={() => setShowCreateFolder(false)}
          onSubmit={handleCreateFolder}
        />
        <UploadModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          currentPath={currentPath}
          onComplete={() => load(currentPath)}
        />
      </div>
    </ManagementLayout>
  );
}
